import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Divider, List, ListItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useMemo } from 'react';

import { TEMPLATE_CONFIGS } from '../../config/templateConfig';
import type { ProposalHeading } from '../../types/proposal';
import AddSectionTrigger from './AddSectionTrigger';
import FooterActions from './ProposalStructure/FooterActions';
import FormatModeSelector from './ProposalStructure/FormatModeSelector';
import LockedField from './ProposalStructure/LockedField';
import TemplateSelector from './ProposalStructure/TemplateSelector';
import UsageGuide from './ProposalStructure/UsageGuide';
import { isFuzzyDuplicate } from './ProposalStructure/utils';
import SortableItem from './SortableItem';

const LEGACY_CONDITIONAL_FIELDS = ['case_studies', 'case_study', 'case_study_subdocs'];

interface ProposalStructureProps {
  proposalStructure: ProposalHeading[];
  setProposalStructure: (items: ProposalHeading[]) => void;
  onRemoveHeading: (index: number) => void;
  isUploadingStructure: boolean;
  onStructureFileUpload: (file: File) => Promise<void>;
  onDownloadStructureTemplate: () => void;
  sensors: SensorDescriptor<SensorOptions>[];
  onDragEnd: (event: DragEndEvent) => void;
  onHeadingUpdate: (id: string, newTitle: string) => void;
  onFocuserUpdate: (id: string, newFocuser: string) => void;
  canLoadExtractedStructure?: boolean;
  canLoadHybridExtractedStructure?: boolean;
  onLoadExtractedStructure?: (mode: 'standard' | 'hybrid') => void;
  mergeFields?: string[];
  selectedTemplate?: string;
  onSaveStructureAsDefault?: () => Promise<void>;
  savedStructureTemplate?: ProposalHeading[] | undefined;
  detectedForms?: string[];
  detectedFormLinks?: Record<string, string>;
  enabledForms?: string[];
  onEnabledFormsChange?: (forms: string[]) => void;
  onDownloadForm?: (url: string, filename: string) => void;
}

const ProposalStructure: React.FC<ProposalStructureProps> = ({
  proposalStructure,
  setProposalStructure,
  onRemoveHeading,
  isUploadingStructure,
  onStructureFileUpload,
  onDownloadStructureTemplate,
  sensors,
  onDragEnd,
  onHeadingUpdate,
  onFocuserUpdate,
  canLoadExtractedStructure = false,
  canLoadHybridExtractedStructure = false,
  onLoadExtractedStructure,
  mergeFields = [],
  selectedTemplate,
  onSaveStructureAsDefault,
  savedStructureTemplate,
  detectedForms = [],
}) => {
  const theme = useTheme();

  const hasForms = detectedForms.length > 0;
  const effectiveFormatMode = hasForms ? 'hybrid' : 'narrative';

  // Determine active template config
  const activeTemplateConfig = useMemo(() => {
    if (selectedTemplate && TEMPLATE_CONFIGS[selectedTemplate]) {
      return TEMPLATE_CONFIGS[selectedTemplate];
    }
    return TEMPLATE_CONFIGS.draft; // Default
  }, [selectedTemplate]);

  // Update isConditionalField to use active config
  const isConditionalField = useCallback(
    (rawName: string): boolean => {
      if (rawName === 'formatted_response') {
        return false;
      }
      const inConfig = activeTemplateConfig?.allowedTags.some((tagConfig) => {
        const tagRaw = tagConfig.tag.replace(/[{}]/g, '').trim();
        return tagRaw === rawName && tagConfig.type === 'subdoc';
      });
      return inConfig || LEGACY_CONDITIONAL_FIELDS.includes(rawName);
    },
    [activeTemplateConfig]
  );

  // Split mergeFields around 'INSERT DRAFT SECTIONS' to show structure context
  // Only show whitelisted fields in this view
  const WHITELIST = ['INSERT DRAFT SECTIONS', 'INSERT EXECUTIVE SUMMARY'];

  const filteredFields = mergeFields.filter((f) => {
    const raw = f.replace(/[{}]/g, '').trim();
    // Allow if in whitelist OR if it's a subdoc in the active template config
    const isConfigSubdoc = activeTemplateConfig?.allowedTags.some(
      (t) => t.type === 'subdoc' && t.tag.replace(/[{}]/g, '').trim() === raw
    );
    return WHITELIST.includes(raw) || isConfigSubdoc;
  });

  // Find where the dynamic sections should be injected
  const responseIndex = filteredFields.findIndex(
    (f) => f.replace(/[{}]/g, '').trim() === 'INSERT DRAFT SECTIONS'
  );

  let beforeFields: string[] = [];
  let afterFields: string[] = [];

  if (responseIndex !== -1) {
    beforeFields = filteredFields.slice(0, responseIndex);
    afterFields = filteredFields.slice(responseIndex + 1);
  } else {
    // If INSERT DRAFT SECTIONS is missing, we assume everything goes at the top.
    beforeFields = filteredFields;
    afterFields = [];
  }

  // Identify duplicates
  const duplicates = new Set<string>();

  // 1. Check against Merge Fields
  const mergeFieldTitles = mergeFields.map((f) => f.replace(/[{}]/g, '').replace(/_/g, ' '));

  for (const item of proposalStructure) {
    // Check against merge fields
    const matchesMergeField = mergeFieldTitles.some((mf) => isFuzzyDuplicate(item.title, mf));
    if (matchesMergeField) {
      duplicates.add(item.id);
    }

    // Check against other headings
    const matchesOtherHeading = proposalStructure.some(
      (other) => other.id !== item.id && isFuzzyDuplicate(item.title, other.title)
    );
    if (matchesOtherHeading) {
      duplicates.add(item.id);
    }
  }

  const formatMergeField = (field: string): string => {
    let clean = field.replace(/[{}]/g, '').trim();

    // Legacy support
    if (clean === 'case_study_subdocs') {
      return 'Relevant Case Studies';
    }

    // Strip INSERT prefix
    clean = clean.replace(/^INSERT\s+/i, '');

    return clean
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleAddHeading = (index: number) => {
    const newHeading: ProposalHeading = {
      id: crypto.randomUUID(),
      title: 'New Section',
      level: 1,
      type: 'default',
    };
    const newStructure = [...proposalStructure];
    newStructure.splice(index, 0, newHeading);
    setProposalStructure(newStructure);
  };

  return (
    <Box>
      <UsageGuide />

      <FormatModeSelector hasForms={hasForms} />

      <TemplateSelector
        effectiveFormatMode={effectiveFormatMode}
        canLoadExtractedStructure={canLoadExtractedStructure}
        canLoadHybridExtractedStructure={canLoadHybridExtractedStructure}
        onLoadExtractedStructure={onLoadExtractedStructure}
        savedStructureTemplate={savedStructureTemplate}
        setProposalStructure={setProposalStructure}
      />

      <Divider sx={{ mb: 2 }} />

      <List
        disablePadding
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          // Overflow hidden removed to allow AddSectionTrigger to expand
        }}
      >
        {/* Header Row */}
        <ListItem
          dense
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.greyContrast,
            py: 1,
            pl: 2,
            pr: 7, // Space for delete icon action
            position: 'relative',
          }}
        >
          <Box sx={{ width: 28, mr: 1, display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.contrastText }}
            >
              LEVEL
            </Typography>
          </Box>
          <Box sx={{ width: 30, mr: 1 }} />

          <Typography
            variant="caption"
            sx={{ flexGrow: 1, fontWeight: 'bold', color: theme.palette.primary.contrastText }}
          >
            SECTION TITLE
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: 150, sm: 250, md: 350 } }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.contrastText, mr: 0.5 }}
            >
              FOCUSER
            </Typography>
            <Tooltip title="Provide a few words to focus the AI generation for this section (e.g., '400 words', 'mention safety policy'). Max 150 chars.">
              <HelpOutlineIcon
                sx={{ color: theme.palette.bidlogiq.camel, fontSize: '1rem', cursor: 'help' }}
              />
            </Tooltip>
          </Box>

          <AddSectionTrigger onAdd={() => handleAddHeading(0)} />
        </ListItem>

        {/* Render Before Fields */}
        {beforeFields.map((field) => (
          <LockedField
            key={field}
            field={field}
            isConditionalField={isConditionalField}
            formatMergeField={formatMergeField}
          />
        ))}

        {/* Render Dynamic Structure */}
        {proposalStructure.length === 0 ? (
          <Box
            sx={(theme) => ({
              p: 2,
              textAlign: 'center',
              color: 'black',
              backgroundColor: theme.palette.bidlogiq.taupe,
            })}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold' }} gutterBottom>
              Extracting recommended structure from tender documents... Please wait.
            </Typography>
            <Typography variant="body2" gutterBottom>
              The structure will be automatically updated when the analysis is complete.
            </Typography>
            <Typography variant="caption" display="block">
              Note: Any changes made before the analysis finishes will be overwritten.
            </Typography>
          </Box>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={proposalStructure} strategy={verticalListSortingStrategy}>
              {proposalStructure.map((item, index) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  item={item}
                  index={index}
                  onRemove={onRemoveHeading}
                  onUpdate={onHeadingUpdate}
                  onFocuserUpdate={onFocuserUpdate}
                  onInsert={handleAddHeading}
                  isDuplicate={duplicates.has(item.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {/* Render After Fields */}
        {afterFields.map((field) => (
          <LockedField
            key={field}
            field={field}
            isConditionalField={isConditionalField}
            formatMergeField={formatMergeField}
          />
        ))}
      </List>

      <FooterActions
        isUploadingStructure={isUploadingStructure}
        onStructureFileUpload={onStructureFileUpload}
        onDownloadStructureTemplate={onDownloadStructureTemplate}
        onSaveStructureAsDefault={onSaveStructureAsDefault}
      />
    </Box>
  );
};

export default ProposalStructure;
